import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Spinner } from '../../../components/ui/spinner';

interface ApproveConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => Promise<void>;
  projectName: string;
  fiscalYearQuarter: string;
  epicCount: number;
  weightedCapEx: number;
  weightedRnD: number;
}

export function ApproveConfirmationModal({
  open,
  onOpenChange,
  onApprove,
  projectName,
  fiscalYearQuarter,
  epicCount,
  weightedCapEx,
  weightedRnD,
}: ApproveConfirmationModalProps) {
  const [isApproving, setIsApproving] = useState(false);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove();
    } catch (error) {
      console.error('ProjectApproval_ Error approving project:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleClose = () => {
    if (!isApproving) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isApproving ? undefined : onOpenChange}>
      <DialogContent className="!w-[calc(100%-2rem)] !max-w-[900px] max-h-[90vh] !top-[calc(50%-8px)] flex flex-col overflow-hidden" showCloseButton={!isApproving}>
        <DialogHeader className="sticky top-0 bg-background z-10 pb-4 border-b shrink-0">
          <DialogTitle>Approve Project</DialogTitle>
          <DialogDescription>
            Review the details below and confirm approval. Once approved, the project will be locked
            at the current percentages.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Project</p>
                  <p className="font-semibold">{projectName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quarter</p>
                  <p className="font-semibold">{fiscalYearQuarter}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total EPICs</p>
                  <p className="font-semibold">{epicCount}</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t grid grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Final CapEx %</p>
                  <p className="text-4xl font-bold text-primary">{weightedCapEx.toFixed(0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Final R&D %</p>
                  <p className="text-4xl font-bold text-primary">{weightedRnD.toFixed(0)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="sticky bottom-0 bg-background z-10 pt-4 border-t shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isApproving}>
            Close
          </Button>
          <Button onClick={handleApprove} disabled={isApproving}>
            {isApproving ? (
              <>
                <Spinner className="mr-2 w-4 h-4" />
                Approving...
              </>
            ) : (
              'Approve'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
